/**
 * 應收帳款明細表的**合計說明區**(REQ-20260901012)。
 *
 * 原本表格底部只有一列「合計」加四個數字(金額 / 折讓 / 已收款 / 未收款),
 * 使用者拿舊會計系統的請款明細來比,說那樣「太潦草」——舊系統把每一項都標了名字,
 * 而且用 `(加)` / `(減)` 前綴讓整條算式看得出來。這裡就是那一塊。
 *
 * **版面比照月結對帳單底部那塊 `dl`**(`features/statements/StatementView.tsx`):
 * 兩欄網格、標籤灰字、數字靠右等寬、最後一行加框線與粗體、整塊收在右側。
 * 兩張都是拿去對外請款的文件,長得一樣使用者才不用學第二套讀法。
 *
 * 兩條算式**必須真的收斂**,不是排版好看而已:
 *  - `本期未稅金額 + (加)營業稅 = 本期合計` —— 稅額直接用「合計 − 未稅」倒推,
 *    不是各單稅率重算,所以恆等成立(與 `StatementView` 把本期出貨拆兩列同一招)。
 *  - `本期合計 − (減)折讓金額 − (減)已收款 = 本期應收總計` ——
 *    後端 `calcOutstanding()` 就是 `outstanding = total − settled − allowance`
 *    (`apps/api/src/common/utils/accounting-amounts.ts`),而 `totalSettled` /
 *    `totalOutstanding` 與這裡加總的 `rows` 是**同一組已交貨的單**
 *    (REQ-20260901008:後端 `billable`、前端 `delivered !== false`),所以逐單成立
 *    就整體成立。
 *
 * ⚠️ **刻意沒有「前期累計未收帳款」/「累計預收貨款」/「現收金額」三項**,
 * 理由寫在 docs/REQ-20260901012 §三 —— 前兩項是月結對帳單才有的期初滾動餘額
 * (REQ-20260831002 §六 明文把「沒有期初 / 期末滾動」列為這張表與對帳單的差別),
 * 第三項的口徑與這張表的「已收款」不同(那是期間內收款,這裡是逐單累計沖帳)。
 */
import type { ArApDetailRow } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { formatQty, type PrintLine } from './print-lines';

export function ArDetailTotalsSummary({
  rows,
  lines,
  totalSettled,
  totalOutstanding,
}: {
  /** 已交貨(列入請款)的單 —— 未稅 / 稅 / 合計 / 折讓都是**單頭**欄位,逐單加一次 */
  rows: ArApDetailRow[];
  /** 攤平後的表身列 —— 只有數量是逐列加總的 */
  lines: PrintLine[];
  /** 後端算好的兩個總計,不在前端重算 */
  totalSettled: string;
  totalOutstanding: string;
}): JSX.Element {
  /*
   * 數量合計:與月結對帳單同一種算法 —— **單位可能不同,只是逐列加總**
   * (`StatementView` 的 `totalQty()` 檔內註解記著同一句)。
   * 版費 / 刀模費那種補出來的列沒有數量(`qty` 是 null),跳過而不是當成 0 ——
   * 當成 0 的結果一樣,但寫出來才看得出那是刻意不計入。
   */
  const totalQty = lines.reduce((sum, l) => (l.qty == null ? sum : sum + Number(l.qty)), 0);
  const subtotal = rows.reduce((sum, r) => sum + Number(r.subtotal), 0);
  const total = rows.reduce((sum, r) => sum + Number(r.total), 0);
  // 折讓沒填當 0 不當「—」:請款文件上的破折號看起來像欄位壞掉(REQ-20260831001 §一.A)
  const allowance = rows.reduce((sum, r) => sum + Number(r.allowanceAmount ?? 0), 0);

  return (
    <section
      className="mt-4 border-t pt-3 text-sm sm:w-96 sm:ml-auto"
      data-testid="ar-detail-totals-summary"
    >
      <dl className="grid grid-cols-2 gap-y-1">
        {/* 數量不能套 formatMoney(那支會四捨五入成整數,0.5 會變成 1) */}
        <dt className="text-slate-500">數量合計</dt>
        <dd className="text-right font-mono">{formatQty(String(totalQty))}</dd>
        <dt className="text-slate-500">本期未稅金額</dt>
        <dd className="text-right font-mono">{formatMoney(subtotal)}</dd>
        {/* 「合計 − 未稅」倒推,不用各單稅率重算 —— 免稅 / 內含稅的單混在一起也對得起來 */}
        <dt className="text-slate-500">(加)營業稅</dt>
        <dd className="text-right font-mono">{formatMoney(total - subtotal)}</dd>
        <dt className="text-slate-500">本期合計</dt>
        <dd className="text-right font-mono">{formatMoney(total)}</dd>
        <dt className="text-slate-500">(減)折讓金額</dt>
        <dd className="text-right font-mono">{formatMoney(allowance)}</dd>
        <dt className="text-slate-500">(減)已收款</dt>
        <dd className="text-right font-mono">{formatMoney(totalSettled)}</dd>
        <dt className="font-medium text-slate-900 border-t pt-1">本期應收總計</dt>
        <dd className="text-right font-mono font-semibold border-t pt-1">
          {formatMoney(totalOutstanding)}
        </dd>
      </dl>
    </section>
  );
}
